import type { AdminMessageInfo } from '@kyumo/common';
import { Bot, Clock, Cog, User } from 'lucide-react';
import { formatDuration } from '../utilities';
import { AdvisorResultCard } from './AdvisorResultCard';
import { CompressionCard } from './CompressionCard';
import { formatCost, formatNumber } from './chart-utils';
import { FileAttachmentCard, FileSubmissionCard } from './FileCards';
import { MarkdownContent, ReasoningCard } from './MarkdownContent';
import { ExternalAgentMessagesCard, SubAgentMessagesCard } from './SubAgentCards';
import type { ExtractedPrompt } from './SystemPromptSection';
import { ToolCallCard } from './ToolCallCard';
import type {
  CompressionPart,
  ExternalAgentMessageInfo,
  MessagePart,
  ReasoningPart,
  SubAgentCompressionInfo,
  SubAgentMessageInfo,
  TextPart,
  ToolCallPart,
  ToolResultPart,
} from './task-detail-types';
import {
  getToolStatus,
  groupToolParts,
  isFileAttachmentPart,
  parseFileSubmissionResult,
} from './task-detail-utils';
import { WidgetPreview } from './WidgetPreview';

/**
 * メッセージ内容のレンダリング（MessageCardとGroupedAssistantCardで共通使用）
 */
function MessageContent({
  message,
  subagentPrompts,
}: {
  message: AdminMessageInfo;
  subagentPrompts?: ExtractedPrompt[] | undefined;
}) {
  const parts = (message.parts ?? []) as MessagePart[];
  const { nonToolParts, toolGroups } = groupToolParts(parts);
  const hasContent = message.content || parts.length > 0;

  if (!hasContent) {
    return <p className="text-muted-foreground italic">（内容なし）</p>;
  }

  return (
    <div className="space-y-3">
      {parts.length > 0 ? (
        <>
          {/* 全パートを元の順序でレンダリング */}
          {(() => {
            type RenderItem =
              | { type: 'nonTool'; index: number; part: MessagePart }
              | {
                  type: 'toolGroup';
                  index: number;
                  toolCallId: string;
                  call: ToolCallPart | undefined;
                  result: ToolResultPart | undefined;
                };

            const renderItems: RenderItem[] = [];

            nonToolParts.forEach(({ index, part }) => {
              renderItems.push({ type: 'nonTool', index, part });
            });

            toolGroups.forEach((group, toolCallId) => {
              renderItems.push({
                type: 'toolGroup',
                index: group.index,
                toolCallId,
                call: group.call,
                result: group.result,
              });
            });

            renderItems.sort((a, b) => a.index - b.index);

            return renderItems.map((item) => {
              const key = `${message.id}-part-${item.index}`;

              if (item.type === 'nonTool') {
                const part = item.part;

                if (part.type === 'text' && 'text' in part) {
                  if (message.role === 'user') {
                    return (
                      <p key={key} className="whitespace-pre-wrap text-foreground">
                        {(part as TextPart).text}
                      </p>
                    );
                  }
                  return (
                    <div key={key} className="prose prose-invert prose-sm max-w-none">
                      <MarkdownContent content={(part as TextPart).text} />
                    </div>
                  );
                }

                if (part.type === 'reasoning' && 'text' in part) {
                  return <ReasoningCard key={key} content={(part as ReasoningPart).text} />;
                }

                if (isFileAttachmentPart(part)) {
                  return <FileAttachmentCard key={key} attachment={part} />;
                }

                if (part.type === 'compression') {
                  return <CompressionCard key={key} part={part as CompressionPart} />;
                }

                return null;
              }

              if (item.type === 'toolGroup') {
                const { call, result } = item;
                const toolName = call?.toolName || result?.toolName || 'unknown';

                let status: 'running' | 'completed' | 'failed' | 'cancelled';
                if (result) {
                  status = result.isError
                    ? 'failed'
                    : result.isCancelled
                      ? 'cancelled'
                      : 'completed';
                } else if (call) {
                  status = getToolStatus(call.status);
                } else {
                  status = 'running';
                }

                const rawSubAgentMessages =
                  call?.streamingSubAgentMessages || result?.subAgentMessages;
                const subAgentMessages = rawSubAgentMessages?.filter(
                  (entry): entry is SubAgentMessageInfo | SubAgentCompressionInfo =>
                    !('type' in entry && entry.type === 'step-usage'),
                );

                const agentName = (() => {
                  if (!result?.result || typeof result.result !== 'object') return undefined;
                  const resultObj = result.result as Record<string, unknown>;
                  if ('agentName' in resultObj && typeof resultObj.agentName === 'string') {
                    return resultObj.agentName;
                  }
                  if (
                    'generatedAgent' in resultObj &&
                    typeof resultObj.generatedAgent === 'object' &&
                    resultObj.generatedAgent !== null
                  ) {
                    const generatedAgent = resultObj.generatedAgent as Record<string, unknown>;
                    if ('name' in generatedAgent && typeof generatedAgent.name === 'string') {
                      return generatedAgent.name;
                    }
                  }
                  return undefined;
                })();

                const matchedSystemPrompt = agentName
                  ? subagentPrompts?.find((p) => p.agentName === agentName)
                  : undefined;

                const externalAgentResult =
                  result &&
                  !result.isError &&
                  toolName === 'external__callAgent' &&
                  result.result &&
                  typeof result.result === 'object'
                    ? (result.result as {
                        messages?: ExternalAgentMessageInfo[];
                        agentName?: string;
                        agentType?: 'a2a' | 'dify';
                      })
                    : null;

                const fileSubmission =
                  result && !result.isError && !result.isCancelled
                    ? parseFileSubmissionResult(toolName, result.result)
                    : null;

                // showWidget のウィジェットプレビュー
                // deferred__execute 経由の場合は _originalToolName で判定
                const effectiveToolName =
                  toolName === 'deferred__execute' &&
                  result?.result &&
                  typeof result.result === 'object' &&
                  '_originalToolName' in (result.result as Record<string, unknown>)
                    ? ((result.result as Record<string, unknown>)._originalToolName as string)
                    : toolName;

                const widgetResult =
                  effectiveToolName === 'showWidget' &&
                  result &&
                  !result.isError &&
                  result.result &&
                  typeof result.result === 'object'
                    ? (result.result as {
                        title?: string;
                        widgetCode?: string;
                        mode?: 'svg' | 'html';
                      })
                    : null;

                return (
                  <div key={key}>
                    <ToolCallCard
                      toolName={toolName}
                      category={call?.category}
                      status={status}
                      intent={call?.intent}
                      args={call?.args}
                      result={result?.result}
                      isError={result?.isError}
                      isCancelled={result?.isCancelled}
                    />
                    {effectiveToolName === 'builtin__advisor' && !!result?.result && (
                      <AdvisorResultCard result={result.result} />
                    )}
                    {widgetResult?.widgetCode && (
                      <WidgetPreview
                        title={widgetResult.title ?? 'widget'}
                        widgetCode={widgetResult.widgetCode}
                        mode={widgetResult.mode ?? 'html'}
                      />
                    )}
                    {fileSubmission && (
                      <FileSubmissionCard
                        files={fileSubmission.files}
                        message={fileSubmission.message}
                      />
                    )}
                    {subAgentMessages && subAgentMessages.length > 0 && (
                      <SubAgentMessagesCard
                        messages={subAgentMessages}
                        agentName={agentName}
                        systemPrompt={matchedSystemPrompt}
                      />
                    )}
                    {externalAgentResult?.messages && externalAgentResult.messages.length > 0 && (
                      <ExternalAgentMessagesCard
                        messages={externalAgentResult.messages}
                        agentName={externalAgentResult.agentName}
                        agentType={externalAgentResult.agentType}
                      />
                    )}
                  </div>
                );
              }

              return null;
            });
          })()}
        </>
      ) : (
        <div className="prose prose-invert prose-sm max-w-none">
          <MarkdownContent content={message.content || ''} />
        </div>
      )}
    </div>
  );
}

/**
 * グループ化されたアシスタントメッセージカードコンポーネント
 */
export function GroupedAssistantCard({
  messages,
  subagentPrompts,
}: {
  messages: AdminMessageInfo[];
  subagentPrompts?: ExtractedPrompt[];
}) {
  // 最後のメッセージから統計情報を取得
  const lastMessage = messages[messages.length - 1];
  const totalUsage = lastMessage?.usage;
  const duration = lastMessage?.duration;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden border-l-4 border-l-green-500">
      {/* ヘッダー（1つだけ表示） */}
      <div className="px-6 py-3 bg-muted/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center">
            <Bot className="w-4 h-4" />
          </div>
          <div>
            <span className="text-foreground font-medium">エージェント</span>
            {lastMessage?.modelInfo && (
              <span className="ml-2 text-xs text-muted-foreground">
                ({lastMessage.modelInfo.provider}/{lastMessage.modelInfo.modelId})
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          {totalUsage && (
            <>
              <span>{formatNumber(totalUsage.totalTokens)} トークン</span>
              {totalUsage.cost !== undefined && (
                <span className="text-yellow-400">{formatCost(totalUsage.cost)}</span>
              )}
            </>
          )}
          {duration && duration > 0 && (
            <span className="inline-flex items-center gap-1 text-blue-400">
              <Clock className="w-3 h-3" />
              {formatDuration(duration)}
            </span>
          )}
        </div>
      </div>

      {/* 各メッセージの本文 */}
      {messages.map((message, index) => (
        <div key={message.id} className={`px-6 py-4 ${index > 0 ? 'border-t border-border' : ''}`}>
          <MessageContent message={message} subagentPrompts={subagentPrompts} />
        </div>
      ))}
    </div>
  );
}

/**
 * メッセージカードコンポーネント
 */
export function MessageCard({
  message,
  subagentPrompts,
}: {
  message: AdminMessageInfo;
  /** サブエージェントのシステムプロンプト一覧（agentName でマッチング） */
  subagentPrompts?: ExtractedPrompt[];
}) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';

  return (
    <div
      className={`bg-card rounded-xl border border-border overflow-hidden ${
        isUser ? 'border-l-4 border-l-blue-500' : ''
      } ${isAssistant ? 'border-l-4 border-l-green-500' : ''} ${
        isSystem ? 'border-l-4 border-l-orange-500' : ''
      }`}
    >
      {/* ヘッダー */}
      <div className="px-6 py-3 bg-muted/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isUser ? (
            <div className="w-8 h-8 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center">
              <User className="w-4 h-4" />
            </div>
          ) : isSystem ? (
            <div className="w-8 h-8 bg-orange-500/20 text-orange-400 rounded-full flex items-center justify-center">
              <Cog className="w-4 h-4" />
            </div>
          ) : (
            <div className="w-8 h-8 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </div>
          )}
          <div>
            <span className="text-foreground font-medium">
              {isUser
                ? 'ユーザー'
                : isAssistant
                  ? 'エージェント'
                  : message.role === 'system'
                    ? 'システムプロンプト'
                    : message.role}
            </span>
            {message.modelInfo && (
              <span className="ml-2 text-xs text-muted-foreground">
                ({message.modelInfo.provider}/{message.modelInfo.modelId})
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          {message.usage && (
            <>
              <span>{formatNumber(message.usage.totalTokens)} トークン</span>
              {message.usage.cost !== undefined && (
                <span className="text-yellow-400">{formatCost(message.usage.cost)}</span>
              )}
            </>
          )}
          {isAssistant && message.duration && message.duration > 0 && (
            <span className="inline-flex items-center gap-1 text-blue-400">
              <Clock className="w-3 h-3" />
              {formatDuration(message.duration)}
            </span>
          )}
          <span>{new Date(message.createdAt).toLocaleTimeString('ja-JP')}</span>
        </div>
      </div>

      {/* 本文 */}
      <div className="px-6 py-4">
        <MessageContent message={message} subagentPrompts={subagentPrompts} />
      </div>
    </div>
  );
}
